import { RecPatch } from "@/dataProvider/tools";
import { PrimaryClassification } from "@/gen/api/encrypt";
import { ClassificationStatisticPointDto, FileCatalogSearchInput } from "@/gen/api/securefile";
import { Skeleton, Typography, useTheme } from "@mui/material";
import { useGetList } from "ra-core";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import Chart from "react-apexcharts";
import { useClassificationRuleAmbinetDataCotnext } from "./ClassificationRuleAmbientDataProvider";
import { debounce } from "lodash";
import { ChartColorScheme } from "./FileCataClassificationStatistics";



const commFormatter = (val: number) => val.toLocaleString();

function useOwnershipStatis(forPlainFiles: boolean, filtersProp?: FileCatalogSearchInput) {
  const [filters, setFilters] = useState(filtersProp)
  const debounceSetFilters = useCallback(debounce((x) => setFilters(x), 300), [])
  useEffect(() => {
    debounceSetFilters(filtersProp)
  }, [debounceSetFilters, filtersProp])
  const resource = forPlainFiles ? "secure-files-ownership-statis-2" : "secure-files-ownership-statis-1";
  const { data, loaded, loading } = useGetList<ClassificationStatisticPointDto&RecPatch>(resource, undefined, undefined, filters);
  return { data, loaded, loading };
}


export interface DataPointClickHandler {
  (
    x: 'point'|'legend', 
    y: {
      label: string;
      value: number;
      dataPoint: ClassificationStatisticPointDto;
    }
  ): void;
}

const OwnershipPieChart: FC<{
  forPlainFiles: boolean;
  onDataPointClick?: DataPointClickHandler;
  filters?: FileCatalogSearchInput;
}> = ({ 
  forPlainFiles,
  onDataPointClick,
  filters,
}) => {
  const theme = useTheme();
  const { data, loaded } = useOwnershipStatis(forPlainFiles, filters);
  const onDataPointClickRef = useRef(onDataPointClick)
  onDataPointClickRef.current = onDataPointClick
  const { classifications } = useClassificationRuleAmbinetDataCotnext()
  const { primaryClassifications, secondaryClassifications } = filters ?? {}
  const onPrimaryClssLevel = 
    (!primaryClassifications || primaryClassifications.length === 0) &&
    (!secondaryClassifications || secondaryClassifications.length === 0)

  // series: if not specified, it's on primary clss level, otherwise on secondary clss level
  const { series, labels, ls, colors } = useMemo(() => {
    const statisDataPoints = Object.values(data)
    return statisDataPoints.reduce((acc, x) => {
      if (onPrimaryClssLevel) {
        const pClss = Number(x.primaryClassification ?? -1)
        acc.labels.push(PrimaryClassification[pClss] ?? `Not Classified`)
        acc.series.push(x.count ?? 0)
        acc.colors.push(ChartColorScheme[pClss as PrimaryClassification] ?? ChartColorScheme['null'])
        acc.ls.push(x)
      } else {
        const sClss = classifications.find(sClss => sClss.secondaryClassificationId === x.secondaryClassificationId)
        if (sClss) {
          acc.labels.push(sClss.text)
          acc.series.push(x.count ?? 0)
          acc.ls.push(x)
        } else if (x.primaryClassification == null && x.secondaryClassificationId === 0) {
          acc.labels.push('Not Classified')
          acc.series.push(x.count ?? 0)
          acc.colors.push(ChartColorScheme['null'])
          acc.ls.push(x)
        }
      }
      return acc
    }, { series: [] as number[], labels: [] as string[], ls: [] as ClassificationStatisticPointDto[], colors: [] as string[] })
  }, [classifications, data, onPrimaryClssLevel]);

  const MAX_LABEL_LEN = 32;
  const truncate = (s: string) =>
    s.length > MAX_LABEL_LEN ? s.slice(0, MAX_LABEL_LEN - 1) + "…" : s;

  const customLabels = labels.map((label) => {
    const isTruncated = label !== truncate(label);
    return isTruncated
      ? `<span title="${label}">${truncate(label)}</span>`
      : label;
  });

  const chartOptions = useMemo(() => ({
    series,
    options: {
      chart: {
        width: 180,
        events: {
          dataPointSelection: (_event: any, _chartContext: any, config: any) => {
            const idx = config.dataPointIndex;
            const label = labels[idx];
            const value = series[idx];
            const dataPoint = ls[idx]
            console.log([_event, _chartContext, config])
            onDataPointClickRef.current?.(_event ? 'point' : 'legend', { label, value, dataPoint });
          },
        },
      },
      labels,
      legend: {
        show: true,
        customLegendItems: customLabels,
      },
      tooltip: { y: { formatter: commFormatter, show: true } },
      theme: { mode: theme.palette.mode },
      colors: colors.length > 0 ? colors : otherColorPicks,
    },
  }), [series, labels, customLabels, theme.palette.mode, colors, ls]);

  // if (!loaded) return <Typography variant="body2" color="text.secondary">Loading…</Typography>;
  if (!loaded) return <Skeleton height={200} />;
  if (series.length === 0) return <Typography variant="body2" color="warning.main">No Data</Typography>;

  return <Chart height="200" type="pie" {...(chartOptions as any)} />;
}

export default OwnershipPieChart;

const otherColorPicks = [
  '#662e9b',
  '#2e294e',
  '#2983ff',
  '#f86624',
  '#f9ce1d',
  '#13d8aa',
  '#d7263d',
  '#7d02eb',
  '#d4526e',
  '#feb019',
  '#4caf50',
  '#f46036',
  '#00e396',
  '#ff1493',
  '#e6b422',
  '#008ffb',
  '#ff6f61',
  '#5a8a3c',
  '#1a3a6b',
  '#6b5bff',
  '#40e0d0',
  '#c7f464',
  '#800020',
  '#6a7fdb',
  '#fff44f',
]
